import React, { useMemo } from 'react';
import { MODULES, ersattAvAnbudV2 } from '../../data/modules';
import { NAV_CATEGORIES } from '../../data/navCategories';
import { useFavorites } from '../../hooks/useFavorites';
import { useCollapsedSections } from '../../hooks/useCollapsedSections';
import { FavoritesSection } from './FavoritesSection';
import { NavSection } from './NavSection';
import { SidebarFooter } from './SidebarFooter';
import { GlobalSearch } from './GlobalSearch/GlobalSearch';
import { ProjectContext } from './ProjectContext/ProjectContext';
import { useProjectContext } from '../../contexts/ProjectContextProvider';
import { useTheme } from '../../contexts/ThemeProvider';
import ProjectChatWidget from '../ProjectChatWidget';
import { useAuth } from '../../auth/AuthProvider';
import { arSystemadminUtanSimulering } from '../../utils/systemadminVy';
import { useUppdateradeAntal } from '../../modules/Instruktioner/hooks/useUppdateradeAntal';
import './navigation.css';

export function filterModulesForUser(modules, { effectiveModuleKeySet, isSystemAdmin, isTenantAdmin, visaPersonliga }) {
  return modules.filter((m) => {
    if (m.searchOnly) return false; // bara i snabbsöket (Årshjul, Mikael F42)
    if (m.systemAdminOnly && !visaPersonliga) return false;
    if (m.adminOnly && !isSystemAdmin) return false;
    if (m.tenantAdminOnly && !isTenantAdmin && !isSystemAdmin) return false;
    // Fail-closed: en modul UTAN nyckel visades tidigare for alla, sa varje
    // ny modul som glomde sin nyckel blev synlig for hela tenanten. Nu
    // kravs ett uttryckligt val i modulkatalogen — antingen en moduleKey
    // eller alwaysVisible: true.
    //
    // Bara tre poster ar avsiktligt nyckellosa (Start, Inkorg, Instruktioner) och de ar
    // markerade alwaysVisible. Rollkravda vyer (adminOnly/tenantAdminOnly)
    // har redan passerat sin kontroll ovan och behover ingen nyckel.
    if (!m.moduleKey) return Boolean(m.alwaysVisible || m.adminOnly || m.tenantAdminOnly);
    if (!effectiveModuleKeySet) return false;
    return effectiveModuleKeySet.has(m.moduleKey) && !ersattAvAnbudV2(m, effectiveModuleKeySet);
  });
}

export function SidebarNav({
  effectiveModuleKeySet = null,
  isSystemAdmin = false,
  isTenantAdmin = false,
  isImpersonating = false,
  onOpenSettings,
  unreadCounts,
  tenantBrand,
  tenantLogoSrc,
  onLogoClick,
}) {
  const { user } = useAuth() || {};
  const visaPersonliga = arSystemadminUtanSimulering(user);
  const { favorites, isFavorite, toggleFavorite } = useFavorites();
  const { isCollapsed, toggle } = useCollapsedSections();
  const { currentProject } = useProjectContext();
  const { theme } = useTheme();
  // Instruktioner som ändrats sedan användaren läste dem visas som siffra i menyn.
  const uppdateradeInstruktioner = useUppdateradeAntal();
  const raknare = useMemo(
    () => ({ ...(unreadCounts || {}), Instruktioner: uppdateradeInstruktioner }),
    [unreadCounts, uppdateradeInstruktioner],
  );

  const visibleModules = useMemo(
    () => filterModulesForUser(MODULES, { effectiveModuleKeySet, isSystemAdmin, isTenantAdmin, visaPersonliga }),
    [effectiveModuleKeySet, isSystemAdmin, isTenantAdmin],
  );

  const itemsByCategory = useMemo(() => {
    const map = {};
    for (const cat of NAV_CATEGORIES) map[cat.key] = [];
    for (const mod of visibleModules) {
      if (map[mod.category]) map[mod.category].push(mod);
    }
    return map;
  }, [visibleModules]);

  const brandName = (tenantBrand && tenantBrand.whitelabelEnabled && tenantBrand.name) || 'KanPlan';
  const showTenantLogo = !!(tenantBrand && tenantBrand.whitelabelEnabled && tenantLogoSrc);

  return (
    <aside className="kp-sidebar" aria-label="Huvudnavigation">
      <button
        type="button"
        className="kp-sidebar-brand"
        onClick={onLogoClick}
        aria-label={`Hem (${brandName})`}
        title={brandName}
      >
        {showTenantLogo ? (
          <img
            src={tenantLogoSrc}
            alt={brandName}
            className="kp-sidebar-brand-logo"
            draggable={false}
          />
        ) : (
          <>
            <span className="kp-sidebar-brand-mark" aria-hidden="true">
              <svg viewBox="0 0 12 12" fill="none" width={14} height={14}>
                <path d="M2 5h3v5H2V5zm5-3h3v8H7V2z" fill="#FFFFFF" />
              </svg>
            </span>
            <span className="kp-sidebar-brand-text">{brandName}</span>
          </>
        )}
      </button>
      <GlobalSearch
        effectiveModuleKeySet={effectiveModuleKeySet}
        isSystemAdmin={isSystemAdmin}
        isTenantAdmin={isTenantAdmin}
        isFavorite={isFavorite}
      />
      <ProjectContext />
      <nav className="kp-sidebar-nav" role="navigation">
        {!isImpersonating && (
          <FavoritesSection
            favorites={favorites}
            collapsed={isCollapsed('favorites')}
            onToggleCollapse={() => toggle('favorites')}
            onToggleFavorite={toggleFavorite}
            unreadCounts={raknare}
            effectiveModuleKeySet={effectiveModuleKeySet}
            isSystemAdmin={isSystemAdmin}
            isTenantAdmin={isTenantAdmin}
          />
        )}
        {NAV_CATEGORIES.map((cat) => {
          const items = itemsByCategory[cat.key] || [];
          if (items.length === 0) return null;
          return (
            <NavSection
              key={cat.key}
              category={cat}
              items={items}
              collapsed={isCollapsed(cat.key)}
              onToggleCollapse={() => toggle(cat.key)}
              isFavorite={isFavorite}
              onToggleFavorite={toggleFavorite}
              unreadCounts={raknare}
            />
          );
        })}
      </nav>
      {currentProject && (isSystemAdmin || !effectiveModuleKeySet || effectiveModuleKeySet.has('p.kommunikation')) ? (
        <ProjectChatWidget
          projectId={currentProject.id}
          contrastMenu={theme === 'contrast'}
          compact={false}
        />
      ) : null}
      <SidebarFooter onOpenSettings={onOpenSettings} />
    </aside>
  );
}
